import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import type { Campaign } from "@/interfaces/campaing";
import type { CampaignStatus } from "@/types/types";

/**
 * How much of a campaign can still be edited, derived from its status alone.
 * `full` — every field is open (draft). `partial` — budget and recipients are locked, everything
 * else (design, reminders, …) is still open (scheduled/active). `none` — the Edit entry point is
 * withdrawn (closed); the per-step locks below treat it exactly like `partial` (this build has no
 * step that is open under `partial` and closed under `none`).
 */
export type CampaignEditability = "full" | "partial" | "none";

/**
 * THE status → editability matrix. Keyed by `string`, not `CampaignStatus`, so a status this build
 * has never heard of falls through to the `?? "none"` below instead of a lookup that TypeScript
 * (wrongly) believes can never miss — mirrors `statusVisual()` in `constants/campaings.ts`, which
 * never throws on an unknown value either.
 */
const EDITABILITY_BY_STATUS: Record<string, CampaignEditability> = {
  [CAMPAIGN_STATUSES.DRAFT]: "full",
  [CAMPAIGN_STATUSES.SCHEDULED]: "partial",
  [CAMPAIGN_STATUSES.ACTIVE]: "partial",
  // A paused campaign is still mid-life (paused from scheduled or active) — edit like its base state.
  [CAMPAIGN_STATUSES.PAUSED]: "partial",
  [CAMPAIGN_STATUSES.CLOSED]: "none",
  [CAMPAIGN_STATUSES.DISABLED]: "partial", // universal link only, still mid-life
  // canceled / archived / deleted stay at the "none" fallback: nothing left to edit.
};

/**
 * draft → "full" · scheduled/active → "partial" · closed → "none".
 *
 * Fail closed: a status outside the 4 known values (a stale bundle meeting a newer API, or bad
 * data) reads as "none" — the safest wrong answer, since it only ever hides more editing, never
 * grants it.
 */
export function campaignEditability(status: CampaignStatus): CampaignEditability {
  return EDITABILITY_BY_STATUS[status] ?? "none";
}

/** Does the "Edit" item appear in the dropdown? (editability !== "none") */
export function canShowEdit(status: CampaignStatus): boolean {
  return campaignEditability(status) !== "none";
}

/**
 * `null` means the campaign builder is open in CREATE mode — there is no persisted campaign yet,
 * so there is nothing to gate. Every status-aware lock below must return `false` for `null`: a
 * regression here would block campaign creation outright, not just editing an existing one.
 */
export function isBudgetLocked(status: CampaignStatus | null): boolean {
  if (status === null) return false;
  return campaignEditability(status) !== "full";
}

/** See `isBudgetLocked` for the meaning of `null`. Governs the recipient table AND gift reminders —
 *  the two Step-2 slices frozen wholesale by payment (server: STEP2_FROZEN_KEYS). */
export function isRecipientsLocked(status: CampaignStatus | null): boolean {
  if (status === null) return false;
  return campaignEditability(status) !== "full";
}

/**
 * Per-field locks for the three campaign-level fields that stay editable AFTER scheduling — send
 * date, expiration, send method. Mirrors the server's assertPostScheduleEditable one-to-one so the
 * padlock the builder draws never disagrees with the 409/422 the PATCH would return. `null`
 * (CREATE mode) and `draft` are always open.
 *
 * The `reason` is the inline note shown beside a locked field (story AC: explain WHY it is locked).
 */
export interface FieldLock {
  locked: boolean;
  reason?: string;
}

const OPEN: FieldLock = { locked: false };

/** Send date freezes this long before the scheduled send — the scheduled-send cron's safety margin. */
export const SEND_LOCK_LEAD_MS = 5 * 60 * 1000;

export const FIELD_LOCK_REASON = {
  sendDateLive: "Send date can't be changed once a campaign is live.",
  sendDateSoon: "The scheduled send is less than 5 minutes away and can no longer be changed.",
  sendDateMultiDate: "Send dates are set per recipient — manage them in Manage Contacts.",
  sendMethod: "Send method is set when you schedule and can't be changed afterward.",
  expiryEnded: "Expiration can't be changed once a campaign has ended.",
} as const;

/** Send method — editable in Draft only (changing it post-checkout would re-mint contacts/links). */
export function sendMethodLock(status: CampaignStatus | null): FieldLock {
  if (status === null || status === CAMPAIGN_STATUSES.DRAFT) return OPEN;
  return { locked: true, reason: FIELD_LOCK_REASON.sendMethod };
}

/** Expiration — editable in Draft / Scheduled / Active; locked only once Closed. */
export function expiryLock(status: CampaignStatus | null): FieldLock {
  if (status === CAMPAIGN_STATUSES.CLOSED) {
    return { locked: true, reason: FIELD_LOCK_REASON.expiryEnded };
  }
  return OPEN;
}

/**
 * Send date — editable only while Scheduled and more than 5 minutes before the send; locked once the
 * campaign is Live/Closed, and not applicable to multi-date (per-recipient) campaigns. `scheduledAt`
 * is the current send instant (null when none); the 5-minute check is best-effort client-side, the
 * server is authoritative.
 */
export function sendDateLock(
  status: CampaignStatus | null,
  scheduledAt: Date | null,
  scheduleMode: string,
  now: number = Date.now(),
): FieldLock {
  if (status === null || status === CAMPAIGN_STATUSES.DRAFT) return OPEN;
  if (status !== CAMPAIGN_STATUSES.SCHEDULED) {
    return { locked: true, reason: FIELD_LOCK_REASON.sendDateLive };
  }
  if (scheduleMode === "different") {
    return { locked: true, reason: FIELD_LOCK_REASON.sendDateMultiDate };
  }
  if (scheduledAt && scheduledAt.getTime() - now <= SEND_LOCK_LEAD_MS) {
    return { locked: true, reason: FIELD_LOCK_REASON.sendDateSoon };
  }
  return OPEN;
}

/**
 * Does the card offer Pause? Only while the campaign is running — reminders are irrelevant.
 *
 * `active` is running by definition: the server derives it only for a paid, sent, unexpired row.
 * `scheduled` is running once its send day has come and its close day has not (the gap before the
 * scheduled-send cron flags it sent). Draft, closed and unknown statuses never pause. Day-granular,
 * because the card only carries calendar days — UTC days, cut from the API's UTC timestamps, so
 * `today` is read in UTC too. `slice(0, 10)` accepts both `YYYY-MM-DD` and a raw Postgres timestamp.
 */
export function canPauseCampaign(
  campaign: Pick<Campaign, "status" | "startDate" | "endDate" | "notificationMode">,
  now: Date = new Date(),
): boolean {
  // Self-send (manual + universal) campaigns have no GFG-side sending to halt — the server refuses to
  // pause them (Sprint Review 2026-10-09), so the card must not offer it. Universal uses Disable instead.
  if (campaign.notificationMode !== "gfg") return false;
  if (campaign.status === CAMPAIGN_STATUSES.ACTIVE) return true;
  if (campaign.status !== CAMPAIGN_STATUSES.SCHEDULED) return false;
  const today = now.toISOString().slice(0, 10);
  return campaign.startDate.slice(0, 10) <= today && today <= campaign.endDate.slice(0, 10);
}

/** Days past a paused campaign's expiry before the nightly cron auto-settles it (mirrors the API). */
export const PAUSED_SETTLE_GRACE_DAYS = 30;

/**
 * The instant a paused-past-expiry campaign auto-settles if nobody resumes it: its expiry + 30 days.
 * Paused-from-active → Closed (unspent to the cause); paused-from-scheduled → Canceled (credits back).
 */
export function pausedSettleDate(endDate: string): Date {
  const d = new Date(endDate);
  d.setUTCDate(d.getUTCDate() + PAUSED_SETTLE_GRACE_DAYS);
  return d;
}

/** Day-granular (UTC) "has this campaign's expiry passed?". */
function isExpiryPast(endDate: string, today: string): boolean {
  return endDate.slice(0, 10) < today;
}

/**
 * Does the card offer Cancel? A campaign that is paid for but has NEVER sent — Scheduled, or Paused
 * from scheduled. Canceling returns its credits to the wallet (terminal). Mirrors the API's gate.
 */
export function canCancelCampaign(campaign: Pick<Campaign, "status" | "pausedFrom">): boolean {
  if (campaign.status === CAMPAIGN_STATUSES.SCHEDULED) return true;
  return (
    campaign.status === CAMPAIGN_STATUSES.PAUSED &&
    campaign.pausedFrom === CAMPAIGN_STATUSES.SCHEDULED
  );
}

/**
 * Does the card offer Archive? Only once gifts can no longer be claimed: a Closed or Canceled
 * campaign, or one paused-from-active whose expiry has already passed. Mirrors the API's gate.
 */
export function canArchiveCampaign(
  campaign: Pick<Campaign, "status" | "pausedFrom" | "endDate">,
  now: Date = new Date(),
): boolean {
  if (campaign.status === CAMPAIGN_STATUSES.CLOSED) return true;
  if (campaign.status === CAMPAIGN_STATUSES.CANCELED) return true;
  const today = now.toISOString().slice(0, 10);
  return (
    campaign.status === CAMPAIGN_STATUSES.PAUSED &&
    campaign.pausedFrom === CAMPAIGN_STATUSES.ACTIVE &&
    isExpiryPast(campaign.endDate, today)
  );
}

/** Does the card offer Restore (un-archive)? Only an archived campaign. */
export function canRestoreCampaign(campaign: Pick<Campaign, "status">): boolean {
  return campaign.status === CAMPAIGN_STATUSES.ARCHIVED;
}

/** Does the card offer Delete? A Draft (nothing paid for) or a Canceled campaign (already unwound). */
export function canDeleteCampaign(campaign: Pick<Campaign, "status">): boolean {
  return (
    campaign.status === CAMPAIGN_STATUSES.DRAFT || campaign.status === CAMPAIGN_STATUSES.CANCELED
  );
}

/** Does the card offer Resume? Only a paused campaign. */
export function canResumeCampaign(campaign: Pick<Campaign, "status">): boolean {
  return campaign.status === CAMPAIGN_STATUSES.PAUSED;
}

/**
 * Does the card offer Disable? Universal Link only, and only while ACTIVE — turning the shared /u/ link
 * off is the universal equivalent of pause (which self-send campaigns never get). Mirrors the API's gate.
 */
export function canDisableCampaign(
  campaign: Pick<Campaign, "status" | "notificationMode">,
): boolean {
  return campaign.notificationMode === "universal" && campaign.status === CAMPAIGN_STATUSES.ACTIVE;
}

/** Does the card offer Enable (turn the universal link back on)? Only a disabled campaign. */
export function canEnableCampaign(campaign: Pick<Campaign, "status">): boolean {
  return campaign.status === CAMPAIGN_STATUSES.DISABLED;
}

/**
 * Does resuming need new dates? Time kept running while paused, so the send date (only a
 * paused-from-scheduled campaign still has one) or the expiry may have lapsed. When true, the card
 * opens the forced date picker instead of a plain resume — which the server would 422 anyway.
 */
export function resumeNeedsDates(
  campaign: Pick<Campaign, "status" | "pausedFrom" | "startDate" | "endDate">,
  now: Date = new Date(),
): boolean {
  if (campaign.status !== CAMPAIGN_STATUSES.PAUSED) return false;
  const today = now.toISOString().slice(0, 10);
  const sendPast =
    campaign.pausedFrom === CAMPAIGN_STATUSES.SCHEDULED && campaign.startDate.slice(0, 10) < today;
  const expiryPast = campaign.endDate.slice(0, 10) < today;
  return sendPast || expiryPast;
}

/**
 * Why no terminal action (Cancel/Archive/Delete/Restore) is offered — the greyed "blocked" note in
 * the menu. Returns null when SOME terminal action IS available (so no note is shown). The one case
 * worth explaining: a sent campaign whose gift links are still redeemable can only be archived later.
 */
export function campaignActionBlockedReason(
  campaign: Pick<Campaign, "status" | "pausedFrom" | "endDate">,
  formatExpiry: (endDate: string) => string,
): string | null {
  if (
    canCancelCampaign(campaign) ||
    canArchiveCampaign(campaign) ||
    canRestoreCampaign(campaign) ||
    canDeleteCampaign(campaign)
  ) {
    return null;
  }
  // Active, or paused-from-active before expiry: links are live, so archive waits for expiry.
  if (campaign.endDate) {
    return `Gift links are redeemable until ${formatExpiry(campaign.endDate)} — can be archived after that`;
  }
  return "Gift links are still active — this campaign can be archived once they expire";
}

/** Below this many causes, the inline summary never grows a "+N more" suffix. */
const CAUSE_SUFFIX_THRESHOLD = 1;

/** 0 or 1 cause → the name only · N > 1 → `${firstCause} +${N - 1} more` */
export function formatCauseSummary(firstCause: string, causeCount: number): string {
  if (causeCount <= CAUSE_SUFFIX_THRESHOLD) return firstCause;
  return `${firstCause} +${causeCount - 1} more`;
}
